import type { AriaAttributes } from 'react';
import { useCallback, cloneElement, forwardRef, isValidElement } from 'react';
import ALink, { type ALinkProps } from 'tradera/components/alink/alink';
import LoadingIcon from '@tradera/blueprint/theme/icons2/loading.svg?jsx';

import styles from './button.module.scss';
import { classNames } from 'tradera/utils/class-names';
import type { OnlyAs, PolyRefFunction } from 'react-polymorphed';

export type ButtonTheme =
    | 'primary'
    | 'highlight'
    | 'action'
    | 'search'
    | 'warning'
    | 'danger'
    | 'light'
    | 'link'
    | 'translucent'
    | 'unbutton'
    | 'text'
    | 'tag'
    | 'mainMenu'
    | 'dropdown';

export type ButtonVariant = 'filled' | 'outline' | 'ghost' | 'inverted';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export type ButtonProps = AriaAttributes &
    Pick<ALinkProps, 'href' | 'rel' | 'target'> &
    React.PropsWithChildren<{
        theme?: ButtonTheme;
        variant?: ButtonVariant;
        /**
         * Default is 'sm'
         */
        size?: ButtonSize;
        disabled?: boolean;
        className?: string;
        wide?: boolean;
        round?: boolean;
        brandedText?: boolean;
        fluid?: boolean;
        linkReverted?: boolean;
        onClick?: (event: React.MouseEvent<HTMLElement>) => void;
        onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
        /**
         * NOTE: If not passing any `className` to the icon it will be set automatically depending on button size and color.
         */
        icon?: React.ReactNode;
        /**
         * Place an icon on the left side of the button. Same as using `icon`.
         */
        iconLeft?: React.ReactNode;
        /**
         * Place an icon on the right side of the button.
         */
        iconRight?: React.ReactNode;
        /**
         * @deprecated Use `iconLeft` or `iconRight` instead to place icons, and `fixedIcon` to fix the icon in place.
         * `position` defaults to `left`
         *
         * { position: 'left' | 'right', variant: 'fixed'}
         */
        iconConfig?: {
            position?: 'left' | 'right';
            variant?: 'fixed';
        };
        /**
         * Place an icon to the outer left or right of the button
         */
        fixedIcon?: boolean;

        loading?: boolean;
        input?: boolean;
        nowrap?: boolean;
        /**
         * Default is 'center'
         */
        align?: 'left' | 'center' | 'right';
        type?: 'submit' | 'button' | 'reset';
        title?: string;
        alt?: string;
        /**
         * By default buttons will shrink when clicked. This disables that behaviour.
         */
        noTransform?: boolean;
    }>;

const polyRef = forwardRef as PolyRefFunction;

const getClassNameFromReactElement = (element: React.ReactNode) => {
    const props = isValidElement(element) ? element?.props : undefined;
    return typeof props === 'object' &&
        props &&
        'className' in props &&
        props.className
        ? (props?.className as string)
        : '';
};

const ButtonIcon = ({
    icon: IconComponent,
    size,
    round,
    className
}: {
    icon?: React.ReactNode;
    size: ButtonProps['size'];
    round: ButtonProps['round'];
    className?: string;
}) => {
    if (!IconComponent) return null;
    if (!isValidElement(IconComponent)) return null;

    const iconClasses = getClassNameFromReactElement(IconComponent);

    return (
        <>
            {cloneElement(IconComponent, {
                ...IconComponent?.props,
                // Set default classes if none are provided
                className: iconClasses
                    ? iconClasses
                    : classNames(
                          'icon',
                          {
                              'icon-xs': size === 'xs',
                              'icon-sm': size === 'sm',
                              'icon-md': size === 'md' || size === 'lg',
                              'icon-lg': round && size === 'lg'
                          },
                          className
                      )
            })}
        </>
    );
};

export const Button = polyRef<
    'button',
    ButtonProps,
    OnlyAs<'button' | 'input' | 'a'>
>(
    (
        {
            theme = 'primary',
            variant = 'filled',
            size = 'sm',
            children,
            disabled,
            wide,
            round,
            className,
            brandedText,
            fluid,
            linkReverted,
            onClick,
            icon: iconProp,
            iconLeft: iconLeftProp,
            iconRight: iconRightProp,
            iconConfig: iconConfigProp,
            href,
            loading,
            input,
            align = 'center',
            nowrap,
            fixedIcon,
            noTransform,
            ...otherProps
        },
        forwardedRef
    ) => {
        const iconConfig = { position: 'left', ...iconConfigProp };

        const isIconOnRight = iconConfig?.position === 'right';
        const isIconOnLeft = iconConfig?.position === 'left';
        const iconLeft = iconLeftProp ?? (isIconOnLeft ? iconProp : null);
        const iconRight = iconRightProp ?? (isIconOnRight ? iconProp : null);
        const isFixedIcon = fixedIcon ?? iconConfig?.variant === 'fixed';

        const ariaDisabled = otherProps['aria-disabled'];

        const buttonClasses = classNames(
            styles.button,
            styles.theme,
            styles[`theme-${theme}`],
            {
                [styles.filled]: variant === 'filled',
                [styles.outline]: variant === 'outline',
                [styles.ghost]: variant === 'ghost',
                [styles.inverted]: variant === 'inverted',
                [styles.brandedText]: brandedText,
                [styles[`size-${size}`]]: !!size && size !== 'sm',
                [styles.wide]: wide,
                [styles.round]: round,
                [styles.fluid]: fluid,
                [styles.loading]: loading,
                [styles.linkReverted]: linkReverted && theme === 'link',
                'text-nowrap text-truncate': nowrap,
                [styles.fixed]: iconConfig.variant === 'fixed',
                ['text-left']: align === 'left',
                ['text-right']: align === 'right',
                // otherwise icons won't be out to the edge on larger sizes when using fixed icons since we pad them a lot.
                [styles.fluidFixedIcon]:
                    fluid && isFixedIcon && theme !== 'unbutton',
                [styles.noTransform]: noTransform
            },
            className
        );

        const onBtnClick = useCallback(
            (
                event: React.MouseEvent<
                    HTMLAnchorElement | HTMLButtonElement,
                    MouseEvent
                >
            ) => {
                if (ariaDisabled === true || ariaDisabled === 'true') return;

                if (typeof onClick === 'function' && !loading) {
                    onClick(event);
                }
            },
            [onClick, loading, ariaDisabled]
        );

        if (input) {
            return <input className={buttonClasses} {...otherProps} />;
        }

        const loadingContent = loading ? (
            <span className={styles.loadingIcon}>
                <LoadingIcon
                    className={classNames('icon icon-spin', {
                        'icon-xs': size === 'xs',
                        'icon-md': size === 'md',
                        'icon-lg': size === 'lg'
                    })}
                />
                <span
                    className="visually-hidden"
                    role="alert"
                    aria-live="assertive">
                    Loading
                </span>
            </span>
        ) : null;

        /**
         * Queue some complexity for handling all different positionings of icons.
         *
         * Since we want to be able to both have icons on the left and right side of
         * the inner content, AND be able to align the text to the left, center or
         * right, AND be able to have icons fixed, we need to do some codez to make
         * things predictable. Sure, we could absolutely position the icons, but
         * that could bring some unexpected behaviour, so let's use flexbox instead.
         *
         * We solve this by having a wrapper around the icon(s) and the content. And
         * then we place elements on both sides of the content, no matter where
         * the icon(s) are. We then flex-grow each element to be able to center the
         * content if we want to.
         */
        const fixedToLeft = isFixedIcon && align !== 'left';
        const fixedToRight = isFixedIcon && align !== 'right';
        const content =
            !!iconLeft || !!iconRight ? (
                <span
                    className={classNames('d-flex align-items-center', {
                        'justify-content-center': align === 'center',
                        'justify-content-end': align === 'right'
                    })}>
                    {!!iconLeft && (
                        <span
                            style={{
                                // if we have a fixed icon but not on the left, we need to flex-grow this element to push the content to center,
                                // Unfortunately bootstrap's flex-fill sets flex: 1 1 auto; but we want flex: 1 1 0%;
                                ...(isFixedIcon && align !== 'left'
                                    ? { flex: 1 }
                                    : {})
                            }}
                            className={classNames('d-flex', {
                                'mr-1': !!children && !fixedToLeft
                            })}>
                            <ButtonIcon
                                icon={iconLeft}
                                size={size}
                                round={round}
                            />
                        </span>
                    )}
                    {/* Weird hack: If we do not have an icon on the left but on the right, and want to center we have to add an empty element here which takes up space.
                    And if we do not add some content inside it, it will take up more height than the other elements, hence the dot. */}
                    {!iconLeft && align === 'center' && isFixedIcon ? (
                        <span
                            className="invisible"
                            // Unfortunately bootstrap's flex-fill sets flex: 1 1 auto; but we want flex: 1 1 0%;
                            style={{ flex: 1 }}>
                            .
                        </span>
                    ) : null}
                    {children}
                    {loadingContent}
                    {/*  Weird hack: If we do not have an icon on the right but on the left, and want to center when have to add an empty element here which takes up space.
                    And if we do not add some content inside it, it will take up more height than the other elements, hence the dot. */}
                    {!iconRight && align === 'center' && isFixedIcon ? (
                        <span
                            className="invisible"
                            // Unfortunately bootstrap's flex-fill sets flex: 1 1 auto; but we want flex: 1 1 0%;
                            style={{ flex: 1 }}>
                            .
                        </span>
                    ) : null}
                    {!!iconRight && (
                        <span
                            style={{
                                ...(isFixedIcon && align !== 'right'
                                    ? { flex: 1 }
                                    : {})
                            }}
                            className={classNames(
                                'd-flex justify-content-end',
                                {
                                    'ml-1': !!children && !fixedToRight
                                }
                            )}>
                            <ButtonIcon
                                icon={iconRight}
                                size={size}
                                round={round}
                            />
                        </span>
                    )}
                </span>
            ) : (
                <>
                    {children}
                    {loadingContent}
                </>
            );

        // An <a> element cannot be disabled, therefore we cannot easily get a disabled behavior for it without modifying a lot of props,
        // so easiest way to solve this is perhaps to just render a disabled <button>.
        return href && !disabled ? (
            <ALink
                href={href}
                className={buttonClasses}
                onClick={onBtnClick}
                ref={forwardedRef}
                {...otherProps}>
                {content}
            </ALink>
        ) : (
            <button
                className={buttonClasses}
                onClick={onBtnClick}
                disabled={disabled}
                {...otherProps}
                ref={forwardedRef}>
                {content}
            </button>
        );
    }
);

Button.displayName = 'Button';
